import { AttributeList, useVisibilityState } from '@lbcamden/lib-react';
import { formatOrFallback, titleCase } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';

import { SectionContainer } from '~/client/components/common/SectionContainer';
import { sensitiveValueAttribute } from '~/client/components/common/SensitiveValue';
import { usePropertyDetails, useReferenceListDisplayValue } from '~/client/hooks/storeHooks';
import { defaultFallbackValue } from '~/common/config/constants';
import { ReferenceListType } from '~/common/types/data';

import { EditButton } from './PropertyAttributes.styled';
import { PropertyAttributesEditDialog } from './PropertyAttributesEditDialog/PropertyAttributesEditDialog';

type Props = {
  propertyId: number;
};

const formatOrFallbackTitleCase = (value?: string) =>
  formatOrFallback(value, titleCase, defaultFallbackValue);

const nextOfKinNameLabel = 'Next of kin - Name';
const nextOfKinTelephoneLabel = 'Next of kin - Phone';
const nextOfKinAddressLabel = 'Next of kin - Address';

export const PropertyAttributes: FunctionComponent<Props> = ({ propertyId }) => {
  const propertyDetails = usePropertyDetails(propertyId);
  const refListDisplayValue = useReferenceListDisplayValue();

  const { show: showEdit, hide: hideEdit, visible: editVisible } = useVisibilityState();

  if (!propertyDetails) {
    return null;
  }

  const formatOrFallbackRefList = (type: ReferenceListType, value: string) =>
    formatOrFallback(value, (v) => refListDisplayValue(type, v), defaultFallbackValue);

  return (
    <SectionContainer title="Property attributes" headerActions={<EditButton onClick={showEdit} />}>
      <AttributeList
        css={(theme) => ({ marginTop: theme.spacing(3) })}
        data={propertyDetails}
        config={[
          'numberOfBedrooms',
          [
            'floorLevel',
            { format: (value: string) => formatOrFallbackRefList('FLOORLEVEL', value) },
          ],
          [
            'adaptation',
            { format: (value: string) => formatOrFallbackRefList('ADAPTATION', value) },
          ],
          [
            'heatingType',
            { format: (value: string) => formatOrFallbackRefList('HEATTYPE', value) },
          ],
          ['accessibleLift', { format: (value: string) => formatOrFallbackRefList('LIFT', value) }],
          [
            'nextOfKinName',
            {
              label: nextOfKinNameLabel,
              format: sensitiveValueAttribute({
                label: nextOfKinNameLabel,
                innerFormat: formatOrFallbackTitleCase,
              }),
            },
          ],
          [
            'nextOfKinTelephone',
            {
              label: nextOfKinTelephoneLabel,
              format: sensitiveValueAttribute({
                label: nextOfKinTelephoneLabel,
                innerFormat: (val) => formatOrFallback(val, undefined, '-'),
              }),
            },
          ],
          [
            'nextOfKinAddress',
            {
              label: 'Next of kin - Address',
              format: sensitiveValueAttribute({
                label: nextOfKinAddressLabel,
                innerFormat: formatOrFallbackTitleCase,
              }),
            },
          ],
        ]}
      />

      <PropertyAttributesEditDialog
        open={editVisible}
        propertyDetails={propertyDetails}
        onClose={hideEdit}
      />
    </SectionContainer>
  );
};
